import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslate } from "src/locales";
import {
  requestService,
  RequestTypeWithPrivilegeDto,
} from "src/api/request/requestService";
import { patientDataService } from "src/api/patient-datas/patientDatasService";

export const useRequestCategoryManagement = (open: boolean, rowID: string) => {
  const { t } = useTranslate();
  const [selectedMainCategory, setSelectedMainCategory] =
    useState("sık-kullanılanlar");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [currentView, setCurrentView] = useState<string>("main");
  const [mainSearchTerm, setMainSearchTerm] = useState("");
  const [sidebarSearchText, setSidebarSearchText] = useState("");
  const [requestTypes, setRequestTypes] = useState<
    RequestTypeWithPrivilegeDto[]
  >([]);
  const [protocolId, setProtocolId] = useState<number | null>(null);

  // Fetch request types and patient info
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [requestTypesResponse, patientInfoResponse] = await Promise.all([
          requestService.getRequestTypesWithPrivileges(),
          patientDataService.all(Number(rowID)),
        ]);

        if (requestTypesResponse && Array.isArray(requestTypesResponse.data)) {
          setRequestTypes(requestTypesResponse.data);
        } else if (Array.isArray(requestTypesResponse)) {
          setRequestTypes(requestTypesResponse);
        }

        if (patientInfoResponse && patientInfoResponse.protid) {
          setProtocolId(patientInfoResponse.protid);
        } else if (
          patientInfoResponse &&
          patientInfoResponse.data &&
          patientInfoResponse.data.protid
        ) {
          setProtocolId(patientInfoResponse.data.protid);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    if (open) {
      fetchInitialData();
    }
  }, [open, rowID]);

  // Generate categories dynamically from API data
  const categories = useMemo(() => {
    const baseCategories = [
      {
        id: "geçmiş",
        name: t("patient-request-service.categories.history"),
        icon: null,
        type: "main",
      },
      {
        id: "sık-kullanılanlar",
        name: t("patient-request-service.categories.frequent"),
        icon: null,
        type: "main",
      },
      {
        id: "tümü",
        name: t("patient-request-service.categories.all"),
        icon: null,
        type: "main",
      },
    ];

    const apiCategories = requestTypes.map((item: any) => {
      const categoryId = `api-${item.objectid}`;
      const hasSubtypes = item.subtypes && item.subtypes.length > 0;

      // Eğer tek bir subtype varsa ve ismi parent ile aynıysa, parent'ı direkt seçilebilir yap
      const hasSingleSameNameSubtype =
        hasSubtypes &&
        item.subtypes.length === 1 &&
        item.subtypes[0].name.toLowerCase().trim() ===
          item.name.toLowerCase().trim();

      return {
        id: categoryId,
        name: item.name,
        icon: hasSingleSameNameSubtype
          ? "plus"
          : hasSubtypes
          ? "arrow"
          : "plus",
        type: "sub" as const,
        parent: "tümü",
        objectid: item.objectid,
        privid: item.privid,
        // Eğer tek aynı isimli subtype varsa, onun bilgilerini parent'a aktar
        subtypeIds: hasSingleSameNameSubtype ? item.subtypes[0].ids : undefined,
        isSingleChild: hasSingleSameNameSubtype,
      };
    });

    const childCategories = requestTypes.flatMap((item: any) => {
      if (!item.subtypes || item.subtypes.length === 0) return [];

      // Eğer tek bir subtype varsa ve ismi parent ile aynıysa, child'ı gösterme
      const hasSingleSameNameSubtype =
        item.subtypes.length === 1 &&
        item.subtypes[0].name.toLowerCase().trim() ===
          item.name.toLowerCase().trim();

      if (hasSingleSameNameSubtype) {
        return []; // Child'ı gösterme
      }

      return item.subtypes.map((subtype: any, index: number) => ({
        id: `api-${item.objectid}-subtype-${index}`,
        name: subtype.name,
        icon: "plus" as const,
        type: "child" as const,
        parent: `api-${item.objectid}`,
        objectid: item.objectid,
        subtypeIds: subtype.ids,
      }));
    });

    // If current view is frequent, show base categories + API parent categories (no child categories)
    if (currentView === "frequent") {
      return [...baseCategories, ...apiCategories];
    }

    return [...baseCategories, ...apiCategories, ...childCategories];
  }, [currentView, requestTypes, t]);

  const handleMainCategorySelect = useCallback((id: string) => {
    setSelectedMainCategory(id);
    if (id === "geçmiş") {
      setCurrentView("history");
    } else if (id === "sık-kullanılanlar") {
      setCurrentView("frequent");
    } else if (id === "tümü") {
      setCurrentView("main");
    } else if (id.startsWith("api-")) {
      // Main category selected (when viewing All)
      // Logic handled in UI
    }
  }, []);

  const handleSubCategorySelect = useCallback((id: string) => {
    setSelectedSubCategory(id);
    if (id.startsWith("api-")) {
      if (selectedMainCategory !== "sık-kullanılanlar") {
        setCurrentView("subcategory");
      }
    }
  }, [selectedMainCategory]);

  const handleCategoryToggle = useCallback((id: string) => {
    setExpandedCategories((prev) =>
      prev.includes(id) ? prev.filter((catId) => catId !== id) : [...prev, id]
    );
  }, []);

  // Filter categories based on sidebar search text
  const filteredCategories = useMemo(() => {
    if (!sidebarSearchText) return categories;
    const lower = sidebarSearchText.toLocaleLowerCase("tr-TR");
    
    // Find all categories that match the search text
    const matches = categories.filter(cat => cat.name.toLocaleLowerCase("tr-TR").includes(lower));
    const keepIds = new Set<string>();
    
    // For each match, add it and all its ancestors to the set
    for (const match of matches) {
      keepIds.add(match.id);
      let current = match;
      // Traverse up to find ancestors
      while(current.parent && current.parent !== "root" && current.parent !== "tümü") {
        const parent = categories.find(c => c.id === current.parent);
        if (parent) {
          keepIds.add(parent.id);
          current = parent;
        } else {
          break;
        }
      }
    }
    
    return categories.filter(cat => keepIds.has(cat.id));
  }, [categories, sidebarSearchText]);

  // Handle sidebar search - switch to "all" view when searching
  const handleSidebarSearch = useCallback((text: string) => {
    setSidebarSearchText(text);
    if (text) {
      // Switch to "tümü" (all) view when searching
      setSelectedMainCategory("tümü");
      setCurrentView("subcategory");
      // Expand all categories that match
      const lower = text.toLocaleLowerCase("tr-TR");
      const matchingParentIds = categories
        .filter(cat => cat.name.toLocaleLowerCase("tr-TR").includes(lower) && cat.parent)
        .map(cat => cat.parent as string)
        .filter(Boolean);
      setExpandedCategories(prev => Array.from(new Set([...prev, ...matchingParentIds])));
    }
  }, [categories]);

  return {
    selectedMainCategory,
    setSelectedMainCategory,
    selectedSubCategory,
    setSelectedSubCategory,
    expandedCategories,
    setExpandedCategories,
    currentView,
    setCurrentView,
    mainSearchTerm,
    setMainSearchTerm,
    sidebarSearchText,
    setSidebarSearchText,
    handleSidebarSearch,
    requestTypes,
    setRequestTypes,
    protocolId,
    setProtocolId,
    categories, // The computed list
    filteredCategories, // Filtered by sidebar search
    handleMainCategorySelect,
    handleSubCategorySelect,
    handleCategoryToggle,
  };
};
